/**
 * Блочная разметка Discord — то, что живёт в начале строки: заголовки «# », «## », «### »,
 * подпись «-# », цитата «> » (соседние строки — одна цитата) и «>>> » — цитата до конца сообщения.
 *
 * Сначала сообщение режется на блоки по строкам, потом каждый блок размечается внутри
 * (жирный, упоминания, ссылки) — как в CommonMark. Блок кода ```…``` при этом целый:
 * строки внутри него ничего не значат, «# » там — просто текст.
 */

export type Block =
  | { kind: 'text'; text: string }
  | { kind: 'heading'; level: 1 | 2 | 3; text: string }
  | { kind: 'subtext'; text: string }
  | { kind: 'quote'; text: string }

// после решёток и дефиса обязателен пробел: «#тег» и «#канал» заголовками не становятся
const HEADING = /^ *(#{1,3}) +(\S.*)$/
const SUBTEXT = /^ *-# +(\S.*)$/
const QUOTE = /^ *>(?!>>) +/
const QUOTE_REST = /^ *>>> +/

/** Разбивает текст на блоки. quotes = false — внутри цитаты: вложенных цитат у Discord нет. */
export function parseBlocks(text: string, quotes = true): Block[] {
  const out: Block[] = []
  let plain = ''
  const push = (block: Block) => {
    // перевод строки перед блоком ему не нужен: блок и так начинается с новой строки
    if (plain.endsWith('\n')) plain = plain.slice(0, -1)
    if (plain) out.push({ kind: 'text', text: plain })
    plain = ''
    out.push(block)
  }

  let pos = 0
  while (pos < text.length) {
    const nl = text.indexOf('\n', pos)
    const end = nl < 0 ? text.length : nl
    // боты присылают и \r\n
    const line = text.slice(pos, end).replace(/\r$/, '')

    if (quotes) {
      const rest = QUOTE_REST.exec(line)
      if (rest) {
        push({ kind: 'quote', text: text.slice(pos + rest[0].length) })
        pos = text.length
        break
      }
      if (QUOTE.test(line)) {
        const lines: string[] = []
        let p = pos
        while (p < text.length) {
          const next = text.indexOf('\n', p)
          const stop = next < 0 ? text.length : next
          const prefix = QUOTE.exec(text.slice(p, stop))
          if (!prefix) break
          lines.push(text.slice(p + prefix[0].length, stop))
          p = stop + 1
        }
        push({ kind: 'quote', text: lines.join('\n') })
        pos = p
        continue
      }
    }

    // строка, в которой открывается блок кода, заголовком не считается: иначе блок разрежется
    const stop = fenceEnd(text, pos, end)
    const heading = stop === end ? HEADING.exec(line) : null
    if (heading) {
      push({ kind: 'heading', level: heading[1]!.length as 1 | 2 | 3, text: heading[2]! })
      pos = end + 1
      continue
    }
    const sub = stop === end ? SUBTEXT.exec(line) : null
    if (sub) {
      push({ kind: 'subtext', text: sub[1]! })
      pos = end + 1
      continue
    }
    plain += text.slice(pos, stop + 1)
    pos = stop + 1
  }
  if (plain) out.push({ kind: 'text', text: plain })
  return out
}

/**
 * Конец строки с учётом блоков кода: если в строке открывается ```, а закрывается ниже,
 * строкой считается всё до конца строки с закрытием. Незакрытые ``` — просто текст.
 */
function fenceEnd(text: string, from: number, end: number): number {
  let i = from
  let stop = end
  for (;;) {
    const open = text.indexOf('```', i)
    if (open < 0 || open >= stop) return stop
    const close = text.indexOf('```', open + 3)
    if (close < 0) return stop
    i = close + 3
    if (i > stop) {
      const nl = text.indexOf('\n', i)
      stop = nl < 0 ? text.length : nl
    }
  }
}

// адрес со схемой, домен (буквы и цифры через точки, зона из букв: «klichat.ru», «кличат.рф») или IP
const ADDRESS = /[a-z][a-z0-9+.-]*:\/\/[^\s/?#]*|(?:[\p{L}\p{N}-]+\.)+\p{L}{2,}|\d{1,3}(?:\.\d{1,3}){3}/giu

/** Имя сервера без «www.» и в одной записи: кириллический домен браузер отдаёт в punycode. */
function host(address: string): string | null {
  try {
    return new URL(address.includes('://') ? address : `http://${address}`).hostname.replace(/^www\./, '')
  } catch {
    return null
  }
}

/**
 * Текст маскированной ссылки сам выглядит как адрес или домен, а ведёт ссылка на другой
 * сервер: «[google.com](https://evil.example)». Такой текст показывать нельзя — вместо него
 * показываем настоящий адрес.
 */
export function misleadingLabel(label: string, url: string): boolean {
  const target = host(url)
  if (!target) return true
  for (const m of label.matchAll(ADDRESS)) {
    if (host(m[0]) !== target) return true
  }
  return false
}
